/** True when a company block covers the entire booking day (not a partial timeslot). */
export const isFullDayCompanyBlock = (block) => {
  if (!block?.startDate || !block?.endDate) return true;
  if (block?.dayType?.includes("Full Day")) return true;
  const start = new Date(block.startDate);
  const end = new Date(block.endDate);
  return (end - start) / (1000 * 60 * 60) >= 10;
};

const SINGAPORE_TZ = "Asia/Singapore";

const getSingaporeDateKey = (dateInput) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: SINGAPORE_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date(dateInput));
};

export const isSameCalendarDayAsBlock = (blockStartDate, date) => {
  const sessionDate = date?.toDate ? date.toDate() : date;
  return (
    getSingaporeDateKey(blockStartDate) === getSingaporeDateKey(sessionDate)
  );
};

/** True when the session falls on a calendar day that has any admin company block. */
export const sessionOnCompanyBlockCalendarDay = (
  sessionStart,
  companyBlockDates
) => {
  if (!sessionStart) {
    return false;
  }
  for (const block of companyBlockDates ?? []) {
    if (isSameCalendarDayAsBlock(block?.startDate, sessionStart)) {
      return true;
    }
  }
  return false;
};

/** Disable calendar date only when a full-day company block applies. */
export const shouldDisableDateForCompanyBlock = (date, companyBlockDates) => {
  for (const block of companyBlockDates ?? []) {
    if (
      isSameCalendarDayAsBlock(block?.startDate, date) &&
      isFullDayCompanyBlock(block)
    ) {
      return true;
    }
  }
  return false;
};

const NIGHT_BOOKING_START_HOUR = 18;
const NIGHT_BOOKING_END_HOUR = 22;
const NIGHT_BOOKING_END_MINUTE = 30;

/**
 * Disable a calendar date when the customer cannot book any slot for the
 * selected session type (e.g. night-only company block + Night session).
 */
export const shouldDisableCalendarDate = (
  date,
  companyBlockDates,
  timePreference
) => {
  if (shouldDisableDateForCompanyBlock(date, companyBlockDates)) {
    return true;
  }

  const isNightSession = timePreference?.includes("Night");
  if (!isNightSession) return false;

  const nightWindowStart = date
    .hour(NIGHT_BOOKING_START_HOUR)
    .minute(0)
    .second(0)
    .millisecond(0)
    .toISOString();
  const nightWindowEnd = date
    .hour(NIGHT_BOOKING_END_HOUR)
    .minute(NIGHT_BOOKING_END_MINUTE)
    .second(0)
    .millisecond(0)
    .toISOString();

  for (const block of companyBlockDates ?? []) {
    if (!isSameCalendarDayAsBlock(block?.startDate, date)) continue;
    if (
      doDateRangesOverlap(
        block.startDate,
        block.endDate,
        nightWindowStart,
        nightWindowEnd
      )
    ) {
      return true;
    }
  }

  return false;
};

const doDateRangesOverlap = (startA, endA, startB, endB) =>
  new Date(startA) < new Date(endB) && new Date(endA) > new Date(startB);

const COMPANY_BLOCK_POST_END_COOLDOWN_MS = 60 * 60 * 1000;

const sessionConflictsWithCompanyBlock = (
  sessionStart,
  sessionEnd,
  blockStart,
  blockEnd
) => {
  if (doDateRangesOverlap(blockStart, blockEnd, sessionStart, sessionEnd)) {
    return true;
  }
  if (!isFullDayCompanyBlock({ startDate: blockStart, endDate: blockEnd })) {
    const sessionStartMs = new Date(sessionStart).getTime();
    const blockEndMs = new Date(blockEnd).getTime();
    if (
      sessionStartMs >= blockEndMs &&
      sessionStartMs < blockEndMs + COMPANY_BLOCK_POST_END_COOLDOWN_MS
    ) {
      return true;
    }
  }
  return false;
};

/** True when a session on calendarDate overlaps any company block on that day. */
export const sessionOverlapsCompanyBlock = (
  sessionStart,
  sessionEnd,
  calendarDate,
  companyBlockDates
) => {
  for (const block of companyBlockDates ?? []) {
    if (!isSameCalendarDayAsBlock(block?.startDate, calendarDate)) continue;
    if (
      sessionConflictsWithCompanyBlock(
        sessionStart,
        sessionEnd,
        block.startDate,
        block.endDate
      )
    ) {
      return true;
    }
  }
  return false;
};
